import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ManageSubscriptionDialog } from "./subscription/ManageSubscriptionDialog";

export const SubscriptionSettings = () => {
  // Fetch subscription data
  const { data: subscription, isLoading: isSubscriptionLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("owner_id", user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error("Erreur lors de la récupération de l'abonnement:", error);
          toast.error("Erreur lors de la récupération de l'abonnement");
          throw error;
        }

        // If no subscription found, return default free plan
        if (!data) {
          console.log("Aucun abonnement trouvé, utilisation du plan gratuit par défaut");
          return {
            plan: "free",
            product_limit: 3
          };
        }

        console.log("Abonnement trouvé:", data);
        return data;
      } catch (error) {
        console.error("Erreur inattendue:", error);
        toast.error("Une erreur est survenue lors de la récupération de l'abonnement");
        throw error;
      }
    },
    // Rafraîchir les données toutes les 10 secondes
    refetchInterval: 10000,
    // Rafraîchir les données quand la fenêtre reprend le focus
    refetchOnWindowFocus: true,
  });

  // Fetch custom limit
  const { data: customLimit, isLoading: isCustomLimitLoading } = useQuery({
    queryKey: ["customLimit"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("custom_product_limits")
          .select("product_limit")
          .eq("owner_id", user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error("Erreur lors de la récupération de la limite personnalisée:", error);
          return null;
        }

        console.log("Limite personnalisée trouvée:", data);
        return data?.product_limit;
      } catch (error) {
        console.error("Erreur lors de la récupération de la limite personnalisée:", error);
        return null;
      }
    }
  });

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case "premium":
        return "bg-[#33C3F0]/10 text-[#33C3F0] border-[#33C3F0]";
      case "standard":
        return "bg-[#F97316]/10 text-[#F97316] border-[#F97316]";
      default:
        return "bg-gray-100 text-gray-600 border-gray-600";
    }
  };

  const isLoading = isSubscriptionLoading || isCustomLimitLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Use custom limit if available, otherwise use subscription limit
  const effectiveLimit = customLimit !== null ? customLimit : subscription?.product_limit;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Abonnement</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium">Plan actuel</p>
            <Badge className={`${getPlanBadgeColor(subscription?.plan)}`}>
              {subscription?.plan === "free" ? "Gratuit" : 
               subscription?.plan === "standard" ? "Standard" : "Premium"}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Limite de produits</p>
            <p className="text-2xl font-bold">
              {effectiveLimit === -1 ? "Illimité" : effectiveLimit}
              {customLimit !== null && customLimit !== subscription?.product_limit && (
                <span className="text-xs text-muted-foreground ml-2">(Personnalisée)</span>
              )}
            </p>
          </div>
        </div>

        <div className="pt-4 border-t">
          <ManageSubscriptionDialog currentPlan={subscription?.plan || "free"} />
        </div>
      </CardContent>
    </Card>
  );
};
